// src/pages/SuperAdmin/UsersManagement.tsx - Gestion globale des utilisateurs (Design moderne avec Cards)

import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getDocs, query, collection, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Modal,
  message,
  Tooltip,
  Input,
  Select,
  Badge,
  Dropdown,
  Menu,
  Empty,
  Spin,
  Avatar,
} from 'antd';
import { matchesSearchInFields } from '../../utils/searchUtils';
import {
  UserOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  TeamOutlined,
  BankOutlined,
  CalendarOutlined,
  UserAddOutlined,
  MoreOutlined,
  MailOutlined,
  PhoneOutlined,
  FilterOutlined,
  SafetyOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useAllBusinesses, useAllUsers, useToggleUserStatus, SuperAdminUser } from '../../hooks/useSuperAdmin';
import UserFormDrawer from './components/UserFormDrawer';
import { useRoleProfilesByBusinessId } from '../../hooks/useRoleProfiles';
import { useEmployees } from '../../hooks/useEmployees';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import './UsersManagement.css';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const UsersManagement: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<(SuperAdminUser & { businessNames: string[] }) | null>(null);
  const { data: users = [], isLoading } = useAllUsers();
  const { data: businesses = [] } = useAllBusinesses();
  const toggleUserMutation = useToggleUserStatus();
  const { data: employees = [] } = useEmployees();
  const { isCSM } = useAuth();
  
  // Extraire tous les businessIds uniques des utilisateurs
  const allBusinessIds = useMemo(() => {
    const uniqueBusinessIds = new Set<string>();
    users.forEach(user => {
      if (user.businessIds) {
        user.businessIds.forEach(id => uniqueBusinessIds.add(id));
      }
    });
    return Array.from(uniqueBusinessIds);
  }, [users]);
  
  // Charger les profils pour chaque business
  const roleProfilesQueries = useQueries({
    queries: allBusinessIds.map(businessId => ({
      queryKey: ['roleProfiles', businessId],
      queryFn: async () => {
        const q = query(
          collection(db, COLLECTIONS.roleProfiles),
          where('businessId', '==', businessId),
          orderBy('name')
        );
        
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            name: data.name,
            description: data.description,
            permissions: data.permissions,
            isActive: data.isActive ?? true,
            isDefault: data.isDefault ?? false,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            createdBy: data.createdBy,
            usageCount: data.usageCount ?? 0,
          };
        });
      },
      enabled: !!businessId,
    })),
  });
  
  // Fusionner tous les profils de tous les businesses
  const allRoleProfiles = useMemo(() => {
    return roleProfilesQueries
      .flatMap(query => query.data || [])
      .filter((profile, index, self) => 
        index === self.findIndex(p => p.id === profile.id)
      );
  }, [roleProfilesQueries]);
  
  // Helper pour obtenir le nom du profil de rôle
  const getRoleProfileName = (roleProfileId?: string) => {
    if (!roleProfileId) return 'Pas de permissions';
    const profile = allRoleProfiles.find(p => p.id === roleProfileId);
    return profile?.name || 'Profil introuvable';
  };
  
  // Helper pour obtenir les profils par business
  const getUserRoleProfilesDisplay = (user: EnrichedUser) => {
    if (user.roleProfileIds && Object.keys(user.roleProfileIds).length > 0) {
      return Object.entries(user.roleProfileIds).map(([businessId, roleProfileId]) => {
        const businessName = businessMap[businessId] || businessId;
        const profileName = getRoleProfileName(roleProfileId);
        return `${businessName}: ${profileName}`;
      }).join(' | ');
    }
    return 'Pas de permissions';
  };

  const businessMap = useMemo(() => {
    return businesses.reduce<Record<string, string>>((acc, business) => {
      acc[business.id] = business.name;
      return acc;
    }, {});
  }, [businesses]);

  type EnrichedUser = SuperAdminUser & { businessNames: string[] };

  const enrichedUsers: EnrichedUser[] = useMemo(() => {
    return users.map(user => ({
      ...user,
      businessNames: user.businessIds.map(id => businessMap[id] || id),
    }));
  }, [users, businessMap]);

  // Statistiques
  const stats = useMemo(() => ({
    totalUsers: enrichedUsers.length,
    activeUsers: enrichedUsers.filter(u => u.isActive).length,
    adminUsers: enrichedUsers.filter(u => {
      if (u.isSuperAdmin) return true;
      if (u.roleProfileIds && Object.keys(u.roleProfileIds).length > 0) {
        return Object.values(u.roleProfileIds).some(roleProfileId => 
          getRoleProfileName(roleProfileId).toLowerCase().includes('admin')
        );
      }
      return false;
    }).length,
    multiBusinessUsers: enrichedUsers.filter(u => u.businessIds.length > 1).length,
  }), [enrichedUsers, allRoleProfiles]);

  // Handlers
  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await toggleUserMutation.mutateAsync({ userId, isActive: !currentStatus });
      message.success(`Utilisateur ${currentStatus ? 'désactivé' : 'activé'} avec succès`);
    } catch (error) {
      message.error('Erreur lors de la modification du statut');
      console.error(error);
    }
  };

  const handleViewUser = (user: EnrichedUser) => {
    Modal.info({
      title: `Détails de ${user.name}`,
      width: 700,
      content: (
        <div style={{ marginTop: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>Email:</Text>
              <div style={{ marginTop: '4px' }}>
                <Space>
                  <MailOutlined style={{ color: '#1890ff' }} />
                  <Text copyable>{user.email}</Text>
                </Space>
              </div>
            </Col>
            <Col span={12}>
              <Text strong>Téléphone:</Text>
              <div style={{ marginTop: '4px' }}>
                {user.phone ? (
                  <Space>
                    <PhoneOutlined style={{ color: '#52c41a' }} />
                    <Text copyable>{user.phone}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </div>
            </Col>
            <Col span={12}>
              <Text strong>Rôle métier:</Text>
              <div style={{ marginTop: '4px' }}>
                {user.role ? (
                  <Tag color="purple">{user.role}</Tag>
                ) : (
                  <Text type="secondary">Pas de rôle</Text>
                )}
              </div>
            </Col>
            <Col span={12}>
              <Text strong>Statut:</Text>
              <div style={{ marginTop: '4px' }}>
                <Tag color={user.isActive ? 'green' : 'red'} icon={user.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                  {user.isActive ? 'Actif' : 'Inactif'}
                </Tag>
              </div>
            </Col>
            <Col span={24}>
              <Text strong>Dernière connexion:</Text>
              <div style={{ marginTop: '4px' }}>
                <Space>
                  <CalendarOutlined />
                  <Text>{user.lastLogin ? dayjs(user.lastLogin).format('DD/MM/YYYY à HH:mm') : 'Jamais'}</Text>
                  {user.lastLogin && (
                    <Text type="secondary">({dayjs(user.lastLogin).fromNow()})</Text>
                  )}
                </Space>
              </div>
            </Col>
            <Col span={24}>
              <Text strong>Businesses et permissions:</Text>
              <div style={{ marginTop: '8px' }}>
                {user.isSuperAdmin ? (
                  <Tag color="red" icon={<CrownOutlined />} style={{ padding: '6px 12px' }}>
                    Super Admin - Accès à tout
                  </Tag>
                ) : user.isCSM ? (
                  <Tag color="blue" icon={<SafetyOutlined />} style={{ padding: '6px 12px' }}>
                    CSM - Customer Success Manager
                  </Tag>
                ) : (
                  <div>
                    {user.businessNames && user.businessNames.length > 0 ? (
                      user.businessNames.map((businessName: string, index: number) => {
                        const businessId = user.businessIds[index];
                        const roleProfileId = user.roleProfileIds?.[businessId];
                        const profileName = roleProfileId 
                          ? getRoleProfileName(roleProfileId)
                          : 'Pas de permissions';
                        
                        return (
                          <div key={businessId} style={{ 
                            marginBottom: '8px',
                            padding: '8px 12px',
                            background: '#f5f5f5',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <BankOutlined style={{ color: '#722ed1' }} />
                            <Tag color="purple">{businessName}</Tag>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              → {profileName}
                            </Text>
                          </div>
                        );
                      })
                    ) : (
                      <Text type="secondary">Aucun business assigné</Text>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
    });
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setDrawerOpen(true);
  };

  const handleEditUser = (user: EnrichedUser) => {
    const updatedUser = enrichedUsers.find(u => u.id === user.id);
    setEditingUser(updatedUser || user);
    setDrawerOpen(true);
  };

  // Filtrage des données
  const filteredUsers = enrichedUsers.filter(user => {
    const matchesText = matchesSearchInFields([user.name, user.email], searchText);

    const userRoleName = user.isSuperAdmin ? 'Super Admin' : getUserRoleProfilesDisplay(user);
    const matchesRole = !roleFilter || userRoleName.includes(roleFilter) || userRoleName === roleFilter;
    const matchesStatus = statusFilter === undefined || user.isActive === statusFilter;
    
    return matchesText && matchesRole && matchesStatus;
  });

  // Rendu d'une card utilisateur
  const renderUserCard = (user: EnrichedUser) => {
    const isTargetSuperAdmin = user.isSuperAdmin === true;
    const disableEditAndToggle = isCSM && isTargetSuperAdmin;

    const menu = (
      <Menu>
        <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleViewUser(user)}>
          Voir détails
        </Menu.Item>
        <Menu.Item 
          key="edit" 
          icon={<EditOutlined />} 
          onClick={() => handleEditUser(user)}
          disabled={disableEditAndToggle}
        >
          Modifier
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item 
          key="toggle" 
          icon={user.isActive ? <LockOutlined /> : <UnlockOutlined />}
          onClick={() => handleToggleStatus(user.id, user.isActive)}
          disabled={disableEditAndToggle}
          danger={user.isActive}
        >
          {user.isActive ? 'Désactiver' : 'Activer'}
        </Menu.Item>
      </Menu>
    );

    // Initiales pour l'avatar
    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    };

    // Couleur de l'avatar basée sur le statut
    const avatarColor = user.isSuperAdmin ? '#ff4d4f' : user.isCSM ? '#1890ff' : '#722ed1';

    return (
      <Card 
        key={user.id}
        className="user-management-card"
        hoverable
        style={{
          marginBottom: '16px',
          borderLeft: `4px solid ${user.isActive ? '#52c41a' : '#d9d9d9'}`,
        }}
      >
        <Row gutter={16} align="middle">
          {/* Avatar et infos principales */}
          <Col xs={24} sm={24} md={12} lg={14}>
            <Space size={16} align="start" style={{ width: '100%' }}>
              {/* Avatar */}
              <Avatar 
                size={64} 
                style={{ 
                  backgroundColor: avatarColor,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}
              >
                {getInitials(user.name)}
              </Avatar>

              {/* Infos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Nom et badges */}
                <div style={{ marginBottom: '8px' }}>
                  <Space wrap size={[8, 4]}>
                    <Text strong style={{ fontSize: '16px' }}>{user.name}</Text>
                    {user.isSuperAdmin && (
                      <Tag color="red" icon={<CrownOutlined />}>Super Admin</Tag>
                    )}
                    {user.isCSM && (
                      <Tag color="blue" icon={<SafetyOutlined />}>CSM</Tag>
                    )}
                    {user.role && !user.isSuperAdmin && !user.isCSM && (
                      <Tag color="purple">{user.role}</Tag>
                    )}
                    <Tag color={user.isActive ? 'green' : 'red'}>
                      {user.isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  </Space>
                </div>

                {/* Email */}
                <div style={{ marginBottom: '4px' }}>
                  <Space size={4}>
                    <MailOutlined style={{ color: '#8c8c8c' }} />
                    <Text type="secondary" style={{ fontSize: '13px' }} copyable>
                      {user.email}
                    </Text>
                  </Space>
                </div>

                {/* Téléphone */}
                {user.phone && (
                  <div style={{ marginBottom: '4px' }}>
                    <Space size={4}>
                      <PhoneOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary" style={{ fontSize: '13px' }} copyable>
                        {user.phone}
                      </Text>
                    </Space>
                  </div>
                )}

                {/* Businesses */}
                {!user.isSuperAdmin && !user.isCSM && (
                  <div style={{ marginTop: '8px' }}>
                    {user.businessNames && user.businessNames.length > 0 ? (
                      <Space wrap size={[4, 4]}>
                        {user.businessNames.slice(0, 3).map((businessName: string, index: number) => {
                          const businessId = user.businessIds[index];
                          const roleProfileId = user.roleProfileIds?.[businessId];
                          const profileName = roleProfileId 
                            ? getRoleProfileName(roleProfileId)
                            : null;
                          
                          return (
                            <Tooltip 
                              key={businessId} 
                              title={profileName ? `${businessName} - ${profileName}` : businessName}
                            >
                              <Tag color="purple" style={{ margin: 0, fontSize: '12px' }}>
                                <BankOutlined style={{ marginRight: '4px' }} />
                                {businessName}
                              </Tag>
                            </Tooltip>
                          );
                        })}
                        {user.businessNames.length > 3 && (
                          <Tag style={{ margin: 0, fontSize: '12px' }}>
                            +{user.businessNames.length - 3} autres
                          </Tag>
                        )}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Aucun business assigné
                      </Text>
                    )}
                  </div>
                )}

                {/* Dernière connexion */}
                <div style={{ marginTop: '8px' }}>
                  <Space size={4}>
                    <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {user.lastLogin 
                        ? `Dernière connexion ${dayjs(user.lastLogin).fromNow()}`
                        : 'Jamais connecté'
                      }
                    </Text>
                  </Space>
                </div>
              </div>
            </Space>
          </Col>

          {/* Actions */}
          <Col xs={24} sm={24} md={12} lg={10}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              marginTop: '16px'
            }}>
              <Space size={8}>
                <Tooltip title="Voir détails">
                  <Button
                    type="default"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewUser(user)}
                  >
                    Détails
                  </Button>
                </Tooltip>
                <Tooltip title={disableEditAndToggle ? 'Un CSM ne peut pas modifier un Super Admin' : 'Modifier'}>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleEditUser(user)}
                    disabled={disableEditAndToggle}
                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                  >
                    Modifier
                  </Button>
                </Tooltip>
                <Dropdown overlay={menu} trigger={['click']}>
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="users-management-container">
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <Title level={2} style={{ marginBottom: '8px' }}>
            <TeamOutlined /> Gestion des Utilisateurs
          </Title>
          <Text type="secondary" style={{ fontSize: '15px' }}>
            Administration globale de tous les utilisateurs du système
          </Text>
        </div>
        <Button 
          type="primary" 
          size="large"
          icon={<UserAddOutlined />} 
          onClick={handleOpenCreate}
          style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
        >
          Créer un utilisateur
        </Button>
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card stat-total">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
            <div className="stat-content">
              <Text type="secondary" style={{ fontSize: '13px' }}>Total</Text>
              <Title level={3} style={{ margin: 0 }}>{stats.totalUsers}</Title>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card stat-active">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
              <TeamOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
            <div className="stat-content">
              <Text type="secondary" style={{ fontSize: '13px' }}>Actifs</Text>
              <Title level={3} style={{ margin: 0 }}>{stats.activeUsers}</Title>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card stat-admin">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
              <CrownOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
            <div className="stat-content">
              <Text type="secondary" style={{ fontSize: '13px' }}>Admins</Text>
              <Title level={3} style={{ margin: 0 }}>{stats.adminUsers}</Title>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card stat-multi">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <BankOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
            <div className="stat-content">
              <Text type="secondary" style={{ fontSize: '13px' }}>Multi-Business</Text>
              <Title level={3} style={{ margin: 0 }}>{stats.multiBusinessUsers}</Title>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '24px' }} className="filters-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12}>
            <Search
              placeholder="Rechercher un utilisateur (nom, email)..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              size="large"
              allowClear
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Select
              placeholder="Filtrer par rôle"
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: '100%' }}
              size="large"
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Option value="admin">Admin</Option>
              <Option value="manager">Manager</Option>
              <Option value="employee">Employé</Option>
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Select
              placeholder="Filtrer par statut"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              size="large"
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Option value={true}>✅ Actif</Option>
              <Option value={false}>❌ Inactif</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Compteur de résultats */}
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
        </Text>
      </div>

      {/* Liste des utilisateurs en cards */}
      <Spin spinning={isLoading} size="large">
        {filteredUsers.length === 0 ? (
          <Card>
            <Empty 
              description="Aucun utilisateur trouvé"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        ) : (
          <div className="users-list">
            {filteredUsers.map(user => renderUserCard(user))}
          </div>
        )}
      </Spin>

      <UserFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingUser(null);
        }}
        initialUser={editingUser || undefined}
        businesses={businesses}
        onSuccess={async () => {
          setEditingUser(null);
        }}
      />
    </div>
  );
};

export default UsersManagement;
